/**
 * Feature 2 (2026-05-28): "My Change Requests" — per-user filing tracker.
 *
 * Mirrors the Checker queue (ChangeRequestsListPage) but oriented toward
 * the user's own outbox: history-first, no action buttons, includes the
 * Checker's decision_notes so the user can read why their filing was
 * approved or rejected.
 *
 * Security: the backend GET /change-requests endpoint already enforces
 * that non-Checker users (RM + staff) see only their own filings (Beta
 * #9+#10, app/routers/change_requests.py line 109-111). The sidebar
 * visibility for this page is also gated to RM + staff so Checkers
 * (admin + compliance) don't see a link that would render their full
 * audit history under a misleading "My" header.
 */
import {
  Badge, Button, Container, Group, Pagination, Stack, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconFilterOff } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChangeRequestsList } from '../hooks/useChangeRequests';
import { CR_ENTITY_TYPE_OPTIONS, CR_KIND_OPTIONS, crEntityLabel } from '../api/changeRequests';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter, hasActiveFilters,
} from '../components/ColumnFilters';
import { SortableTh, nextSort, sortParam, type SortState } from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';
import type { ChangeRequestStatus } from '../api/types';
import { CHANGE_REQUEST_KIND_LABELS } from '../api/types';
import { EntityFriendlyLabel } from '../components/EntityFriendlyLabel';

const PAGE_SIZE = 50;
type MyCrSortKey = 'requested' | 'entity' | 'kind' | 'status' | 'decider' | 'decided' | 'notes';

type StatusFilter = ChangeRequestStatus | 'ALL';

// #47 polish (Pedro's 2026-06-12 smoke catch, §3h): the population
// selector renders as the SAME button row the Checker queue uses —
// the previous dropdown was the only status selector in the app that
// wasn't a row of always-visible controls. Friendly labels kept
// ("Approved", not the internal APPLIED). Full pills-vs-buttons
// unification stays #48.
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'PENDING',   label: 'Pending' },
  { value: 'APPLIED',   label: 'Approved' },
  { value: 'REJECTED',  label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ALL',       label: 'All' },
];

function statusColor(s: ChangeRequestStatus): string {
  switch (s) {
    case 'PENDING':   return 'yellow';
    case 'APPLIED':   return 'green';
    case 'REJECTED':  return 'red';
    case 'CANCELLED': return 'gray';
  }
}

function statusLabel(s: ChangeRequestStatus): string {
  // The backend uses 'APPLIED' internally for approved-and-applied. From the
  // filer's perspective, the more natural word is "Approved".
  switch (s) {
    case 'APPLIED': return 'Approved';
    default:        return s.charAt(0) + s.slice(1).toLowerCase();
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  // 2026-05-28T09:21:00 -> "2026-05-28 09:21"
  return iso.substring(0, 10) + ' ' + iso.substring(11, 16);
}

export function MyChangeRequestsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  // ── #47 (2026-06-12): per-column predicate filters (C-132), server-side.
  // The old "Period" quick-select was REPLACED by the Filed-at header date
  // range (one axis, one control — same replace-don't-duplicate rule as the
  // #46 global search boxes). Filtering moved client→server, which also
  // made the pagination honest (it previously sliced an at-most-50-row
  // first page from the server).
  const [filedFrom, setFiledFrom] = useState('');
  const [filedTo, setFiledTo] = useState('');
  const [entityF, setEntityF] = useState<string[]>([]);
  const [kindF, setKindF] = useState<string[]>([]);
  // P41 phase 2 (2026-10-15): header sort, server-side across pages; null = newest first.
  const [sort, setSortState] = useState<SortState<MyCrSortKey> | null>(null);
  function onSort(k: MyCrSortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({ filedFrom, filedTo, entityF, kindF });
  function clearFilters() {
    setFiledFrom(''); setFiledTo(''); setEntityF([]); setKindF([]);
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  // The backend already scopes to caller's own requests for RM/staff.
  //
  // Freshness: status changes on this page come from a DIFFERENT user
  // session (Compliance approving in their own browser), so the global
  // 30s staleTime + no-refocus default would leave the user staring at
  // stale "Pending" rows. Override with always-refetch-on-mount and a
  // 30s polling interval — the page feels live without an explicit
  // refresh.
  const apiStatus = statusFilter === 'ALL' ? undefined : statusFilter;
  const { data, isLoading, error } = useChangeRequestsList(apiStatus, {
    refetchOnMount: 'always',
    refetchInterval: 30_000,
    staleTime: 0,
  }, {
    requested_from: filedFrom || undefined,
    requested_to: filedTo || undefined,
    entity_types: entityF.length > 0 ? entityF : undefined,
    kinds: kindF.length > 0 ? kindF : undefined,
    sort: sortParam(sort),
    offset: (page - 1) * PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const paginated = data?.items ?? [];

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>My Change Requests</Title>
        <Text c="dimmed" size="sm">
          Edits you filed that need Compliance approval. Click any row to see
          the diff and audit trail.
        </Text>

        <Group>
          <Text>Filter by status:</Text>
          {STATUS_OPTIONS.map((o) => (
            <StatusExplained key={o.value} entity="change_request" status={o.value}>
              <Button
                variant={statusFilter === o.value ? 'filled' : 'outline'}
                size="xs"
                onClick={() => { setStatusFilter(o.value); setPage(1); }}
              >
                {o.label}
              </Button>
            </StatusExplained>
          ))}
        </Group>

        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 phase 2 (2026-10-15): every header sorts (server-side). */}
              <SortableTh label="Filed at" col="requested" sort={sort} onSort={onSort} w={150}
                filtered={filedFrom !== '' || filedTo !== ''} />
              <SortableTh label="Entity" col="entity" sort={sort} onSort={onSort}
                filtered={entityF.length > 0} />
              <SortableTh label="Kind" col="kind" sort={sort} onSort={onSort} w={110}
                filtered={kindF.length > 0} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={110} />
              <SortableTh label="Decided by" col="decider" sort={sort} onSort={onSort} w={150} />
              <SortableTh label="Decided at" col="decided" sort={sort} onSort={onSort} w={135} />
              <SortableTh label="Checker's comment" col="notes" sort={sort} onSort={onSort} />
            </Table.Tr>
            {/* #47: per-column filter row (C-132). The old "Period" select
                was replaced by the Filed-at date range (one axis, one
                control). Status stays owned by the Status select above. */}
            <Table.Tr>
              <Table.Th>
                <ColumnDateRangeFilter from={filedFrom} to={filedTo}
                  onChange={(f, t) => { setFiledFrom(f); setFiledTo(t); setPage(1); }}
                  ariaLabelPrefix="Filed" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={entityF} onChange={paged(setEntityF)}
                  options={CR_ENTITY_TYPE_OPTIONS} allLabel="All" ariaLabel="Filter by entity type" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={kindF} onChange={paged(setKindF)}
                  options={CR_KIND_OPTIONS} allLabel="All" ariaLabel="Filter by kind" />
              </Table.Th>
              <Table.Th />
              <Table.Th />
              <Table.Th />
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr><Table.Td colSpan={7}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>
            )}
            {/* #47 / C-132: two-tier empty state. */}
            {!isLoading && paginated.length === 0 && (
              <Table.Tr><Table.Td colSpan={7}>
                {filtersActive ? (
                  <Group gap="xs" justify="center" py="sm">
                    <Text c="dimmed" size="sm">No filings match the current filters.</Text>
                    <Button size="compact-xs" variant="subtle"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear filters
                    </Button>
                  </Group>
                ) : (
                  <Text c="dimmed">
                    You haven't filed any change requests
                    {statusFilter !== 'ALL' && ` with status "${statusLabel(statusFilter as ChangeRequestStatus)}"`}
                    . When you edit a record that needs Compliance approval, your filing will appear here.
                  </Text>
                )}
              </Table.Td></Table.Tr>
            )}
            {paginated.map((cr) => (
              <Table.Tr key={cr.id} style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/change-requests/${cr.id}`)}>
                <Table.Td>
                  <Text size="sm" ff="monospace">{formatDateTime(cr.requested_at)}</Text>
                </Table.Td>
                <Table.Td>
                  {cr.entity_id ? (
                    /* #49 (2026-06-13, C-134): pass the server-resolved label
                       as the fast path — kills the per-row alive-only GET
                       fan-out (which 404'd + rendered "unresolved · <uuid>"
                       for soft-deleted DELETE targets). `?? undefined` so a
                       null label falls back to the legacy client-side path. */
                    <EntityFriendlyLabel
                      entityType={cr.entity_type}
                      entityId={cr.entity_id}
                      preResolvedLabel={cr.entity_friendly_label ?? undefined}
                    />
                  ) : (
                    <Text size="sm" c="dimmed">{crEntityLabel(cr.entity_type)} (new)</Text>
                  )}
                  {/* Phase 5 (C-160): same high-impact flag the checker queue
                      shows, so the maker sees their filing's blast radius. */}
                  {cr.impact?.is_high_impact && (
                    <Badge size="xs" color="orange" variant="light" mt={4}
                      leftSection={<IconAlertTriangle size={11} />}>
                      High impact
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td><Badge variant="light">{CHANGE_REQUEST_KIND_LABELS[cr.kind] ?? cr.kind}</Badge></Table.Td>
                <Table.Td>
                  <StatusExplained entity="change_request" status={cr.status}>
                    <Badge color={statusColor(cr.status)} variant="filled">
                      {statusLabel(cr.status)}
                    </Badge>
                  </StatusExplained>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {cr.decided_by_name ?? (cr.decided_by ? cr.decided_by.substring(0, 8) : '—')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">{formatDateTime(cr.decided_at)}</Text>
                </Table.Td>
                <Table.Td>
                  {cr.decision_notes ? (
                    <Tooltip label={cr.decision_notes} multiline w={320}
                      styles={{ tooltip: { whiteSpace: 'pre-line' } }}>
                      <Text size="sm" lineClamp={2} style={{ cursor: 'help' }}>
                        {cr.decision_notes}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text size="sm" c="dimmed">—</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data?.total ?? 0} filings</Text>
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
